import { decamelizeDeep } from '@vpr/restful';

import { AnyObject, Id, Maybe } from '@vimeo/core/types/generic';

import {
  sendApiRequest,
  TApiMethodParameters,
  sendConnectionRequest,
  TConnectionMethodParameters,
  TConnectionSupportedMethodParams,
  withConnectionSupport,
} from './base';
import { ERequestMethod } from '../types/api';
import { EComposerSessionType, TComposerSessionId } from '../types/composer_session';
import {
  EInteractionAuthOption,
  IInteractionRoomCredentials,
  IInteractionRoomInfo,
} from '../types/interaction_room';
import { getCurrentTimeZoneOffset } from '../utils/general/time_zone';

/**
 * Download interaction history in a csv file for specified connection.
 * Add local timezone if param / query is not present.
 *
 * Handles BLOB downloading and saving.
 */
export async function downloadInteractionHistoryByConnection(
  params: TConnectionMethodParameters,
  clientTimeZone: string = getCurrentTimeZoneOffset(),
): Promise<Blob> {
  const hasTimezone: boolean = Boolean(
    params.connection.uri.indexOf('timezone') !== -1 || (params.query && params.query['timezone']),
  );

  const query: AnyObject = params.query || {};

  if (!hasTimezone) {
    query['timezone'] = encodeURIComponent(clientTimeZone);
  }

  return sendConnectionRequest({ ...params, query });
}

export interface IGetInteractionRoomInfoParams {
  roomUnlistedHash?: Maybe<string>;
  roomPassword?: Maybe<string>;
  roomJwtToken?: Maybe<string>;
}

/**
 * Load composer session info based on current session type/id.
 */
export function getInteractionRoomInfo(
  {
    sessionType,
    sessionId,
    gctlConfig,
    fields = [],
  }: TApiMethodParameters<{
    sessionId: TComposerSessionId;
    sessionType: TComposerSessionId;
  }>,
  params?: IGetInteractionRoomInfoParams,
): Promise<IInteractionRoomInfo> {
  return sendApiRequest({
    method: ERequestMethod.GET,
    path: `/interaction_rooms/${sessionType}/${sessionId}`,
    gctlConfig,
    query: decamelizeDeep(params as IGetInteractionRoomInfoParams),
    fields,
  });
}

export interface IPostInteractionRoomAuthBody {
  authType: EInteractionAuthOption;
  roomDisplayName?: Maybe<string>;
  roomGuestCode?: Maybe<string>;
  roomUnlistedHash?: Maybe<string>;
  roomPassword?: Maybe<string>;
  roomEmail?: Maybe<string>;
  roomJwtToken?: Maybe<string>;
}

/**
 * Load composer session info based on current session type/id.
 */
export function postInteractionRoomAuth(
  params: TConnectionSupportedMethodParams<{
    sessionId: TComposerSessionId;
    sessionType: TComposerSessionId;
  }>,
  authParams?: IPostInteractionRoomAuthBody,
): Promise<IInteractionRoomCredentials> {
  return withConnectionSupport(
    ERequestMethod.POST,
    params,
    ({ sessionId, sessionType }) => ({
      path: `/interaction_rooms/${sessionType}/${sessionId}/auth`,
    }),
    { body: decamelizeDeep(authParams as IPostInteractionRoomAuthBody) },
  );
}

// todo: rename to ERoomCredentialType
export enum ECredentialsType {
  PASSWORD = 'password',
  HASHED_PASSWORD = 'hashed_password',
}

export interface IRoomCredentialsCheckResponse {
  roomId: Id;
  roomType: EComposerSessionType;
  credentialsStatus: { type: ECredentialsType; isValid: boolean };
}

export type TRoomCredentialsCheckParams = TConnectionSupportedMethodParams<{
  sessionId: TComposerSessionId;
  sessionType: EComposerSessionType;
}>;

export interface IRoomCredentialsCheckCredentials {
  type: ECredentialsType;
  value: string;
}

/**
 * Validate interaction room auth credentials.
 */
export function postRoomCredentialsCheck(
  params: TRoomCredentialsCheckParams,
  credentials: IRoomCredentialsCheckCredentials,
): Promise<IRoomCredentialsCheckResponse> {
  return withConnectionSupport(
    ERequestMethod.POST,
    params,
    ({ sessionId, sessionType }) => ({
      path: `/interaction_rooms/${sessionType}/${sessionId}/credentials_check`,
    }),
    {
      body: decamelizeDeep({
        credentialsType: credentials.type,
        credentialsValue: credentials.value,
      }),
    },
  );
}
